import { publicHref } from '../lib/publicUrls';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { emitSemanticEvent } from '../lib/semanticEvents';

const menus = [
  {
    label: 'Programs',
    items: [
      ['All Programs', '/programs'],
      ['Personal Training', '/programs/personal-training'],
      ['MX4', '/programs/mx4'],
      ['Reformer Pilates', '/programs/reformer-pilates'],
      ['Swimming Lessons', '/programs/swim'],
      ['Kids & Family', '/programs/kids-and-family'],
      ['Massage Therapy', '/programs/massage'],
    ],
  },
  {
    label: 'Schedule',
    items: [
      ['Full Schedule', '/schedule'],
      ['Cardio', '/schedule/cardio'],
      ['Mind & Body', '/schedule/mind-body'],
      ['Weight Training', '/schedule/weight-training'],
    ],
  },
  {
    label: 'Amenities',
    items: [
      ['Amenities Overview', '/amenities'],
      ['Facility Rentals', '/amenities/rentals'],
      ['Pool & Aquatics', '/amenities/pool'],
      ['Kids Club', '/amenities/kids-club'],
      ['Recovery & Massage', '/amenities/hydro-massage'],
    ],
  },
  {
    label: 'Our Club',
    items: [
      ['Our Story', '/about-us'],
      ['Our Coaches', '/our-coaches'],
      ['Partnerships', '/about-us/partnerships'],
      ['Hot Topics', '/blog'],
      ['Contact Us', '/contact'],
    ],
  },
];

// Wide screens keep the most-used destinations one click away; every page is
// also listed in the full menu.
const shortcuts = [
  ['Programs', '/programs'],
  ['Schedule', '/schedule'],
  ['Membership', '/membership'],
];

const memberLoginUrl = 'https://deltavac.clubautomation.com/';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const menuPanelRef = useRef(null);

  const recordTourIntent = (placement) => {
    emitSemanticEvent('book_tour_cta_click', {
      page_path: location.pathname,
      cta_placement: placement,
      component_context: 'global_header',
      destination: '/book-a-tour',
      location_context: 'unspecified',
    });
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const updateScrolled = () => setScrolled(window.scrollY > 24);
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
    return () => window.removeEventListener('scroll', updateScrolled);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    // Lets page-level overlays, such as the homepage tour invitation, step aside.
    document.documentElement.classList.add('dv-menu-open');
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => menuPanelRef.current?.querySelector('a')?.focus());

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        requestAnimationFrame(() => menuButtonRef.current?.focus());
        return;
      }

      if (event.key !== 'Tab') return;

      const focusables = [
        menuButtonRef.current,
        ...Array.from(menuPanelRef.current?.querySelectorAll('a[href]') || []),
      ].filter(Boolean);
      const first = focusables[0];
      const last = focusables.at(-1);

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // On wide screens the menu does not cover the page; a click outside the
    // header closes it.
    const handlePointerDown = (event) => {
      if (!headerRef.current?.contains(event.target)) setMenuOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.documentElement.classList.remove('dv-menu-open');
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [menuOpen]);

  const isHomeTop = location.pathname === '/' && !scrolled;

  return (
    <header
      ref={headerRef}
      className={[
        'dv-site-header',
        isHomeTop ? 'dv-site-header--transparent' : 'dv-site-header--solid',
        menuOpen ? 'dv-site-header--menu-open' : '',
      ].filter(Boolean).join(' ')}
      data-testid="site-header"
    >
      <div className="dv-site-header__inner">
        <nav className="dv-site-header__shortcuts" aria-label="Primary navigation">
          {shortcuts.map(([label, path]) => (
            <Link key={path} to={publicHref(path)}>{label}</Link>
          ))}
        </nav>

        <Link to="/" className="dv-site-header__logo" aria-label="Delta Valley Health Club home">
          <img src="/Delta-Valley-Logo.png" alt="Delta Valley Health Club" />
        </Link>

        <div className="dv-site-header__actions">
          <a className="dv-member-link" href={publicHref(memberLoginUrl)} target="_blank" rel="noreferrer">
            Member Login
          </a>
          <Link
            className="dv-button dv-button--light dv-button--header"
            to={publicHref("/book-a-tour")}
            onClick={() => recordTourIntent('header_desktop')}
          >
            Book a Tour
          </Link>
          <button
            ref={menuButtonRef}
            type="button"
            className="dv-menu-toggle"
            onClick={() => setMenuOpen((value) => !value)}
            aria-expanded={menuOpen}
            aria-controls="site-menu"
          >
            <span>{menuOpen ? 'Close' : 'Menu'}</span>
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>

      <nav
        ref={menuPanelRef}
        id="site-menu"
        className="dv-site-menu"
        aria-label="Site menu"
        hidden={!menuOpen}
      >
        <div className="dv-site-menu__grid">
          {menus.map((menu, index) => (
            <section key={menu.label} aria-labelledby={`site-menu-group-${index}`}>
              <h2 id={`site-menu-group-${index}`}>{menu.label}</h2>
              {menu.items.map(([label, path]) => (
                <Link key={path} to={publicHref(path)} onClick={() => setMenuOpen(false)}>{label}</Link>
              ))}
            </section>
          ))}
        </div>
        <div className="dv-site-menu__actions">
          <Link to={publicHref("/membership")} onClick={() => setMenuOpen(false)}>Explore Membership</Link>
          <a href={publicHref(memberLoginUrl)} target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}>Member Login</a>
          <Link
            className="dv-button dv-button--light"
            to={publicHref("/book-a-tour")}
            onClick={() => {
              recordTourIntent('header_mobile');
              setMenuOpen(false);
            }}
          >
            Book a Tour
          </Link>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
